<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Schedule Planning" backTo="service">
      <router-link
          :to="{ name: 'schedule-create' }"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>New Schedule</span>
      </router-link>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <div class="flex items-center ">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>

              <StaffAutocomplete
                  v-if="currentTab === 'booked' || currentTab === 'completed'"
                  class="ml-6 w-full lg:w-1/5 xl:w-1/5 2xl:w-1/5"
                  v-model="selectedEngineerInput"
                  :forceSelection="true"
              />

              <input
                  type="text"
                  class="ml-6 w-full lg:w-auto rounded-l bge-input bge-input-spacing"
                  placeholder="Search "
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
            </div>

            <div v-if="currentTab=='completed'" class="flex items-center justify-end mt-5" >
              <label class="mr-2 whitespace-nowrap ">Status:</label>
              <Dropdown
                  v-model="currentTabState.selectedScheduleStatus"
                  :options="scheduleStatusOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Status"
                  class="w-2/11 lg:w-60"
                  @change="triggerSearch"
              >
                <template #option="slotProps">
                  <div :class="getScheduleStatusClass(slotProps.option.value)" class="px-2 py-1 rounded">
                    {{ slotProps.option.label }}
                  </div>
                </template>
              </Dropdown>

            </div>
          </div>
        </div>

        <TabContent :currentTab="currentTab">
          <template v-if="currentTab === 'all_expiring'" v-slot:all_expiring>
            <DataTableExpandableComponent
                :columns="columnsGroupedCustomer"
                key="all_expiring"
                dataKey="customer_id"
                expansionMode="equipment"
                :showActionButton="true"
                :rowClass="rowClass"
                @action-click="onCreateScheduleClick"
                @equipment-updated="onEquipmentUpdated"
            >
              <template v-slot:equipment-count-slot="{ rowData }">
                {{ rowData.equipment_count || 0 }}
              </template>
              <template v-slot:nearest-expiry-slot="{ rowData }">
                <span v-if="rowData.nearest_expiry" :class="isExpiringSoon(rowData.nearest_expiry) ? 'text-red-600 font-semibold' : ''">
                  {{ formatTimestamp(rowData.nearest_expiry) }}
                </span>
                <span v-else class="text-gray-300">—</span>
              </template>
            </DataTableExpandableComponent>
          </template>

          <template v-else-if="currentTab === 'booked'" v-slot:booked>
            <DataTableExpandableComponent
                :columns="columnsBooked"
                key="booked"
                dataKey="schedule_id"
                :rowClass="rowClass"
            >
              <template v-slot:owner-city-slot="{ rowData }">
                <div>{{ getCity(rowData) }}</div>
              </template>

              <template v-slot:owner-postcode-slot="{ rowData }">
                <div>{{ getPostcode(rowData) }}</div>
              </template>

              <template v-slot:equipment-count-slot="{ rowData }">
                {{ rowData.equipment_count || 0 }}
              </template>

              <template v-slot:engineer-count-slot="{ rowData }">
                <span
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    @click.stop="openEngineerPanel(rowData)"
                >
                  {{ rowData.engineer_count || 0 }}
                </span>
              </template>

              <template v-slot:job-type-slot="{ rowData }">
                <span class="px-2 py-0.5 rounded text-xs font-semibold inline-block" :class="jobTypeBadgeClass(rowData)">
                  {{ formatJobType(rowData) }}
                </span>
              </template>

              <template v-slot:start-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.start_date) }}
              </template>

              <template v-slot:end-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.end_date) }}
              </template>

              <template v-slot:job-id-slot="{ rowData }">
                <router-link
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    :to="{ name: 'schedule-edit', params: { schedule_id: rowData.schedule_id } }"
                >
                  {{ rowData.job_id }}
                </router-link>
              </template>

              <template v-slot:status-badge-slot="{ rowData }">
                <span :class="getScheduleStatusClass(rowData.status)" class="px-3 py-1 rounded text-xs font-semibold inline-block">
                  {{ getScheduleStatusLabel(rowData.status) }}
                </span>
              </template>

              <template v-slot:cancel-slot="{ rowData }">
                <div class="flex justify-end">
                  <AppButton
                      v-if="rowData && rowData.status === 'NotStarted'"
                      color="red"
                      label=""
                      :icon="true"
                      title="Cancel Schedule"
                      @click.native.stop="openCancelDialog(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="calendar-cancel" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableExpandableComponent>
          </template>

          <template v-else-if="currentTab === 'unbooked'" v-slot:unbooked>
            <DataTableExpandableComponent
                :columns="columnsGroupedCustomer"
                key="unbooked"
                dataKey="customer_id"
                expansionMode="equipment"
                :showActionButton="true"
                :rowClass="rowClass"
                @action-click="onCreateScheduleClick"
                @equipment-updated="onEquipmentUpdated"
            >
              <template v-slot:equipment-count-slot="{ rowData }">
                {{ rowData.equipment_count || 0 }}
              </template>
              <template v-slot:nearest-expiry-slot="{ rowData }">
                <span v-if="rowData.nearest_expiry" :class="isExpiringSoon(rowData.nearest_expiry) ? 'text-red-600 font-semibold' : ''">
                  {{ formatTimestamp(rowData.nearest_expiry) }}
                </span>
                <span v-else class="text-gray-300">—</span>
              </template>
            </DataTableExpandableComponent>
          </template>

          <template v-else-if="currentTab === 'completed'" v-slot:completed>
            <DataTableExpandableComponent
                :columns="columnsCompleted"
                key="completed"
                dataKey="schedule_id"
            >
              <template v-slot:owner-city-slot="{ rowData }">
                <div>{{ getCity(rowData) }}</div>
              </template>

              <template v-slot:owner-postcode-slot="{ rowData }">
                <div>{{ getPostcode(rowData) }}</div>
              </template>

              <template v-slot:equipment-count-slot="{ rowData }">
                {{ rowData.equipment_count || 0 }}
              </template>

              <template v-slot:engineer-count-slot="{ rowData }">
                <span
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    @click.stop="openEngineerPanel(rowData)"
                >
                  {{ rowData.engineer_count || 0 }}
                </span>
              </template>

              <template v-slot:job-type-slot="{ rowData }">
                <span class="px-2 py-0.5 rounded text-xs font-semibold inline-block" :class="jobTypeBadgeClass(rowData)">
                  {{ formatJobType(rowData) }}
                </span>
              </template>

              <template v-slot:completed-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.completed_at) }}
              </template>

              <template v-slot:job-id-slot="{ rowData }">
                <router-link
                    class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    :to="{ name: 'my-schedule-job-details', params: { job_id: rowData.job_id } }"
                >
                  {{ rowData.job_id }}
                </router-link>
              </template>

              <template v-slot:status-badge-slot="{ rowData }">
                <span :class="getScheduleStatusClass(rowData.status)" class="px-3 py-1 rounded text-xs font-semibold inline-block">
                  {{ getScheduleStatusLabel(rowData.status) }}
                </span>
              </template>
            </DataTableExpandableComponent>
          </template>

          <template v-else-if="currentTab === 'follow_up'" v-slot:follow_up>
            <DataTableComponent
                :columns="columnsFollowUp"
                @row-click="onRowClick"
                key="follow_up"
                :rowClass="rowClass"
            >
              <template v-slot:owner-city-slot="{ rowData }">
                <div>{{ getCity(rowData) }}</div>
              </template>

              <template v-slot:owner-postcode-slot="{ rowData }">
                <div>{{ getPostcode(rowData) }}</div>
              </template>

              <template v-slot:followup-status-slot="{ rowData }">
                <span
                    class="px-3 py-1 rounded text-xs font-semibold inline-block"
                    :class="getFollowUpStatusClass(rowData.status)"
                >
                  {{ getFollowUpStatusLabel(rowData.status) }}
                </span>
              </template>

              <template v-slot:followup-origin-job-slot="{ rowData }">
                <span v-if="rowData.origin_schedule && rowData.origin_schedule.job_id">
                  <router-link
                      class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                      :to="{ name: 'my-schedule-job-details', params: { job_id: rowData.origin_schedule.job_id } }"
                  >
                    {{ rowData.origin_schedule.job_id }}
                  </router-link>
                </span>
                <span v-else class="text-gray-400 text-xs">—</span>
              </template>

              <template v-slot:followup-next-job-slot="{ rowData }">
                <span v-if="rowData.scheduled_schedule && rowData.scheduled_schedule.job_id">
                  <router-link
                      class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                      :to="{ name: 'my-schedule-job-details', params: { job_id: rowData.scheduled_schedule.job_id } }"
                  >
                    {{ rowData.scheduled_schedule.job_id }}
                  </router-link>
                </span>
                <span v-else class="text-gray-400 text-xs">Not scheduled</span>
              </template>

              <template v-slot:followup-scheduled-date-slot="{ rowData }">
                {{ rowData.scheduled_date ? formatTimestamp(rowData.scheduled_date) : '-' }}
              </template>

              <template v-slot:followup-created-date-slot="{ rowData }">
                {{ rowData.created_at ? formatTimestamp(rowData.created_at) : '-' }}
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <portal to="overlay-outlet">
      <Panel :showing="engineerPanelOpen" :title="'Engineers — ' + engineerPanelTitle" @close="closeEngineerPanel">
        <div v-if="engineerPanelLoading" class="flex items-center justify-center py-10">
          <Spinner :color="'text-black'" :size="8" />
        </div>
        <div v-else>
          <div v-if="!engineerPanelData.length" class="text-gray-400 text-center py-8">No engineers assigned.</div>
          <div v-for="g in engineerPanelData" :key="g.engineer_id" class="mb-3 p-3 border rounded-lg">
            <div class="flex items-center justify-between">
              <div class="font-semibold">{{ g.name }}</div>
              <div class="text-xs text-gray-500">{{ g.days.length }} assigned day{{ g.days.length !== 1 ? 's' : '' }}</div>
            </div>
            <div class="mt-2 space-y-1">
              <div
                  v-for="d in g.days"
                  :key="d.assignment_id"
                  class="text-sm text-gray-600 flex items-center gap-2"
              >
                <span>{{ formatTimestamp(d.scheduled_date) }}</span>
                <span v-if="d.start_time" class="text-gray-500">{{ d.start_time }}</span>
                <span v-if="d.start_time && d.end_time" class="text-gray-500">- {{ d.end_time }}</span>
                <span
                    class="ml-auto px-2 py-0.5 rounded text-xs"
                    :class="{
                      'bg-blue-100 text-blue-700': d.status === 'Assigned',
                      'bg-yellow-100 text-yellow-700': d.status === 'InProgress',
                      'bg-green-100 text-green-700': d.status === 'Completed',
                      'bg-gray-100 text-gray-500': d.status === 'Cancelled',
                    }"
                >{{ d.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Engineer/Schedule/Components/DataTableComponent.vue";
import DataTableExpandableComponent from "@/views/HR/Engineer/Schedule/Components/DataTableExpandableComponent.vue";
import {mapActions, mapState} from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import AppButton from "@/components/Button.vue";
import ScheduleService from "@/services/scheduleService";
import { groupAssignmentsByEngineer } from "./groupAssignments";
import { formatJobType } from "@/utils/jobTypeFormat";

const IsAuthorized = () => import("@/components/Auth/IsAuthorized.vue");

export default {
  mixins: [notificationMixin],
  components: {
    StaffAutocomplete,
    PageHeader,
    Spinner,
    Panel,
    IsAuthorized,
    TabHeaders,
    TabContent,
    DataTableComponent,
    DataTableExpandableComponent,
    Icon,
    Calendar,
    Dropdown,
    AppButton,
  },
  data() {
    return {
      engineerPanelOpen: false,
      engineerPanelLoading: false,
      engineerPanelData: [],
      engineerPanelTitle: '',
      tabs: [
        { id: "all_expiring", label: "All Expiring" },
        { id: "unbooked", label: "Unscheduled", subtitle: "Serv / Cal" },
        { id: "booked", label: "Scheduled" },
        { id: "completed", label: "Completed" },
        { id: "follow_up", label: "Follow-up" },
      ],

      columnsGroupedCustomer: [
        {field: "customer_name", header: "Customer", sortable: true, custom: false, width: '25%'},
        {field: "city", header: "Town/City", sortable: false, custom: false, width: '14%'},
        {field: "postcode", header: "Postcode", sortable: false, custom: false, width: '10%'},
        {field: "contact_phone", header: "Contact", sortable: false, custom: false, width: '14%'},
        {field: "nearest_expiry", header: "Nearest Expiry", sortable: true, custom: true, slotName: "nearest-expiry-slot", width: '14%'},
        {field: "equipment_count", header: "Equipment", sortable: false, custom: true, slotName: "equipment-count-slot", width: '10%'},
      ],

      columnsBooked: [
        {field: "customer.name", header: "Customer", sortable: true, custom: false, width: '13%'},
        {field: "customer.default_address.city", header: "Town/City", sortable: false, custom: true, slotName: "owner-city-slot", width: '8%'},
        {field: "customer.default_address.postcode", header: "Postcode", sortable: false, custom: true, slotName: "owner-postcode-slot", width: '7%'},
        {field: "job_type", header: "Job Type", sortable: false, custom: true, slotName: "job-type-slot", width: '10%'},
        {field: "equipment_count", header: "Equipment", sortable: false, custom: true, slotName: "equipment-count-slot", width: '6%'},
        {field: "engineer_count", header: "Engineers", sortable: false, custom: true, slotName: "engineer-count-slot", width: '6%'},
        {field: "job_id", header: "Job ID", sortable: false, custom: true, slotName: "job-id-slot", width: '8%'},
        {field: "start_date", header: "Start Date", sortable: true, custom: true, slotName: "start-date-slot", width: '9%'},
        {field: "end_date", header: "End Date", sortable: false, custom: true, slotName: "end-date-slot", width: '9%'},
        {field: "start_time", header: "Start Time", sortable: false, custom: false, width: '6%'},
        {field: "end_time", header: "End Time", sortable: false, custom: false, width: '6%'},
        {field: "status", header: "Status", sortable: false, custom: true, slotName: "status-badge-slot", width: '8%'},
        {field: "actions", header: "", sortable: false, custom: true, slotName: "cancel-slot", width: '4%'},
      ],




      columnsCompleted: [
        {field: "customer.name", header: "Customer", sortable: true, custom: false, width: '20%'},
        {field: "customer.default_address.city", header: "Town/City", sortable: false, custom: true, slotName: "owner-city-slot", width: '11%'},
        {field: "customer.default_address.postcode", header: "Postcode", sortable: false, custom: true, slotName: "owner-postcode-slot", width: '9%'},
        {field: "job_type", header: "Job Type", sortable: false, custom: true, slotName: "job-type-slot", width: '11%'},
        {field: "equipment_count", header: "Equipment", sortable: false, custom: true, slotName: "equipment-count-slot", width: '7%'},
        {field: "engineer_count", header: "Engineers", sortable: false, custom: true, slotName: "engineer-count-slot", width: '7%'},
        {field: "completed_at", header: "Completed Date", sortable: true, custom: true, slotName: "completed-date-slot", width: '11%'},
        {field: "job_id", header: "Job ID", sortable: false, custom: true, slotName: "job-id-slot", width: '11%'},
        {field: "status", header: "Status", sortable: false, custom: true, slotName: "status-badge-slot", width: '11%'},
      ],

      columnsFollowUp: [
        {
          field: "customer.name",
          header: "Customer",
          sortable: true,
          custom: false,
          width: "16%",
        },
        {
          field: "customer.default_address.city",
          header: "Town/City",
          sortable: false,
          custom: true,
          slotName: "owner-city-slot",
          width: "10%",
        },
        {
          field: "customer.default_address.postcode",
          header: "Postcode",
          sortable: false,
          custom: true,
          slotName: "owner-postcode-slot",
          width: "8%",
        },
        {
          field: "equipment.product_code",
          header: "Code",
          sortable: true,
          custom: false,
          width: "9%",
        },
        {
          field: "equipment.serial_number",
          header: "Serial Number",
          sortable: true,
          custom: false,
          width: "12%",
        },
        {
          field: "origin_schedule.job_id",
          header: "From Job",
          sortable: false,
          custom: true,
          slotName: "followup-origin-job-slot",
          width: "9%",
        },
        {
          field: "scheduled_schedule.job_id",
          header: "Next Job",
          sortable: false,
          custom: true,
          slotName: "followup-next-job-slot",
          width: "9%",
        },
        {
          field: "status",
          header: "Status",
          sortable: false,
          custom: true,
          slotName: "followup-status-slot",
          width: "8%",
        },
        {
          field: "scheduled_date",
          header: "Next Visit",
          sortable: true,
          custom: true,
          slotName: "followup-scheduled-date-slot",
          width: "9%",
        },
        {
          field: "created_at",
          header: "Created",
          sortable: true,
          custom: true,
          slotName: "followup-created-date-slot",
          width: "10%",
        },
      ],

      scheduleStatusOptions: [
        { label: 'All', value: null },
        { label: 'Completed', value: 'Completed' },
        { label: 'Completed + Further Work', value: 'CompletedWithFurtherWork' },
        { label: 'Cancelled', value: 'Cancelled' },
        { label: 'Ready for Invoice', value: 'ReadyForInvoice' },
        { label: 'Invoiced', value: 'Invoiced' },
        { label: 'Closed', value: 'Closed' },
      ],

    };
  },
  computed: {
    ...mapState("schedule", ["currentTab", "searchText", "selectedEngineer", "searchDate"]),

    ...mapState("schedule", {
      currentTabState: (state) => state.tabs[state.currentTab],
    }),

    searchTextInput: {
      get() {
        return this.currentTabState.searchText;
      },
      set(value) {
        this.$store.commit("schedule/UPDATE_SEARCH_TEXT", {tab: this.currentTab, searchText: value});
      },
    },

    selectedEngineerInput: {
      get() {
        return this.currentTabState.selectedEngineer;
      },
      set(value) {
        this.$store.commit("schedule/UPDATE_SELECTED_ENGINEER", {tab: this.currentTab, selectedEngineer: value});
      },
    },

    fromDateInput: {
      get() {
        const fromDate = this.currentTabState.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {
        this.$store.commit("schedule/UPDATE_FROM_DATE", {tab: this.currentTab, fromDate: value});
        this.triggerSearch();
      }
    },

    toDateInput: {
      get() {
        const toDate = this.currentTabState.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("schedule/UPDATE_TO_DATE", {tab: this.currentTab, toDate: value});
        this.triggerSearch();
      }
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("schedule", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
    ]),

    getField(row, path) {
      if (!row || !path) return "";
      const regexArr = /([^[.\]])+/g;
      const props = path.match(regexArr) || [];
      return props.reduce((acc, prop) =>
          (acc && typeof acc === "object" && prop in acc) ? acc[prop] : (Array.isArray(acc) && !isNaN(prop)) ? acc[Number(prop)] : "", row
      );
    },

    getPostcode(rowData) {
      if (rowData.customer && rowData.customer.default_address && rowData.customer.default_address.postcode) {
        return rowData.customer.default_address.postcode;
      }
      if (rowData.owner_info && Array.isArray(rowData.owner_info.addresses) && rowData.owner_info.addresses.length > 0) {
        return rowData.owner_info.addresses[0].postcode || 'N/A';
      }
      return 'N/A';
    },

    getCity(rowData) {
      if (rowData.customer && rowData.customer.default_address && rowData.customer.default_address.city) {
        return rowData.customer.default_address.city;
      }
      if (rowData.owner_info && Array.isArray(rowData.owner_info.addresses) && rowData.owner_info.addresses.length > 0) {
        return rowData.owner_info.addresses[0].city || 'N/A';
      }
      return 'N/A';
    },

    rowClass(data) {
      if (data && data.is_confirmed === true) {
        return 'row-scheduled';
      }

      const startOfToday = this.$moment().startOf('day').unix();
      const tenDaysAhead = this.$moment().add(10, 'days').unix();

      const expiry = data.nearest_expiry
          || (data.calibration_with_max_expiry && data.calibration_with_max_expiry.cert_expiry_date)
          || null;

      if (expiry !== undefined && expiry !== null) {
        if (expiry < startOfToday) {
          return 'row-danger';
        }
        if (expiry <= tenDaysAhead) {
          return 'row-warning';
        }
      }

      return '';
    },

    getDefaultAddressPostcode(addressArray) {
      const defaultAddress = addressArray.find(address => address.is_default);
      return defaultAddress ? defaultAddress.postcode : 'N/A';
    },
    getDefaultPeoplePhone(peopleArray) {
      const defaultPhone = peopleArray.find(people => people.is_default);
      return defaultPhone ? defaultPhone.mobile || defaultPhone.telephone : 'N/A';
    },
    isExpiringSoon(epoch) {
      if (!epoch) return false;
      return this.$moment.unix(epoch).isBefore(this.$moment().startOf('day'));
    },
    onRowClick(event) {
      this.Edit(event.data);
    },
    onCreateScheduleClick(rowData) {
      if (rowData && rowData.customer_id) {
        this.$router.push({ name: "schedule-create", query: { customer_id: rowData.customer_id } });
      }
    },
    formatTimestamp(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },
    formatJobType,
    jobTypeBadgeClass(rowData) {
      if (!rowData) return 'bg-gray-100 text-gray-600';
      if (rowData.is_site_visit || rowData.is_install) {
        return 'bg-orange-100 text-orange-800 border border-orange-200';
      }
      return 'bg-indigo-50 text-indigo-800 border border-indigo-100';
    },
    onEquipmentUpdated() {
      this.setTab(this.currentTab);
    },
    async openCancelDialog(rowData) {
      if (!rowData || !rowData.schedule_id) return;
      const confirmation = await this.confirmAction({
        message: `Cancel schedule ${rowData.job_id || rowData.schedule_id}? This will release any equipment locks and cancel active engineer assignments.`,
        header: 'Confirm Cancel',
        acceptLabel: 'Yes, Cancel Schedule',
        rejectLabel: 'No',
      });
      if (!confirmation) return;
      try {
        await this.$store.dispatch('schedule/cancelSchedule', {
          scheduleId: rowData.schedule_id,
          payload: {},
        });
        this.notifySuccess('Schedule cancelled.');
        this.setTab(this.currentTab);
      } catch (error) {
        const msg = (error && error.data && error.data.message) || "Couldn't cancel the schedule.";
        this.notifyError(msg);
      }
    },
    onTabSelected(newTab) {
      this.setTab(newTab);
    },
    setTab(newTab) {
      this.$store.dispatch("schedule/updateTab", newTab);
    },
    triggerSearch() {
      this.setTab(this.currentTab);
    },

    async openEngineerPanel(rowData) {
      if (!rowData || !rowData.schedule_id) return;
      this.engineerPanelOpen = true;
      this.engineerPanelLoading = true;
      this.engineerPanelData = [];
      this.engineerPanelTitle = rowData.job_id || rowData.schedule_id;
      try {
        const data = await ScheduleService.listAssignments(rowData.schedule_id, '?limit=100');
        this.engineerPanelData = groupAssignmentsByEngineer(data.data || []);
      } catch (err) {
        console.error('Error loading engineers:', err);
        this.engineerPanelData = [];
      } finally {
        this.engineerPanelLoading = false;
      }
    },
    closeEngineerPanel() {
      this.engineerPanelOpen = false;
      this.engineerPanelData = [];
    },

    Edit(rowObj) {
      const tab = this.currentTab;

      let routeName;
      let params = {};
      let query = {};

      if (tab === "booked" && rowObj.schedule_id) {
        routeName = "schedule-edit";
        params.schedule_id = rowObj.schedule_id;
      } else if ((tab === "all_expiring" || tab === "unbooked") && rowObj.customer_id) {
        routeName = "schedule-create";
        query.customer_id = rowObj.customer_id;
        query.fromTab = tab;
        const fmt = d => d && this.$moment(d).format("DD/MM/YYYY");
        const from = fmt(this.currentTabState.fromDate);
        const to = fmt(this.currentTabState.toDate);
        if (from) query.fromDate = from;
        if (to) query.toDate = to;
      } else if (tab === "completed" && rowObj.job_id) {
        routeName = "my-schedule-job-details";
        params.job_id = rowObj.job_id;
      } else if (tab === "follow_up") {
        if (rowObj.scheduled_schedule && rowObj.scheduled_schedule.schedule_id) {
          routeName = "schedule-edit";
          params.schedule_id = rowObj.scheduled_schedule.schedule_id;
        } else {
          routeName = "schedule-create";
          if (rowObj.customer && rowObj.customer.contact_id) {
            query.customer_id = rowObj.customer.contact_id;
            query.fromTab = "follow_up";
          }
        }
      }
      else {
        routeName = "schedule-create";
      }

      this.$router.push({ name: routeName, params, query });
    },

    getScheduleStatusLabel(status) {
      switch (status) {
        case "Completed":
          return "Completed";
        case "CompletedWithFurtherWork":
          return "Completed (Further Work)";
        case "Cancelled":
          return "Cancelled";
        case "InProgress":
          return "In Progress";
        case "NotStarted":
          return "Not Started";
        case "ReadyForInvoice":
          return "Ready for Invoice";
        case "Invoiced":
          return "Invoiced";
        case "Closed":
          return "Closed";
        default:
          return status || "Unknown";
      }
    },

    getScheduleStatusClass(status) {
      switch (status) {
        case "Completed":
          return "bg-green-500 text-white";
        case "CompletedWithFurtherWork":
          return "bg-red-500 text-white";
        case "Cancelled":
          return "bg-gray-400 text-white";
        case "InProgress":
          return "bg-blue-500 text-white";
        case "ReadyForInvoice":
          return "bg-purple-700 text-white";
        case "Invoiced":
          return "bg-gray-500 text-white";
        case "Closed":
          return "bg-black text-white";
        default:
          return "bg-gray-200 text-gray-700";
      }
    },
    getFollowUpStatusLabel(status) {
      switch (status) {
        case "open":
          return "Open";
        case "scheduled":
          return "Scheduled";
        case "done":
          return "Done";
        default:
          return status || "Unknown";
      }
    },
    getFollowUpStatusClass(status) {
      switch (status) {
        case "open":
          return "bg-yellow-500 text-white";
        case "scheduled":
          return "bg-blue-500 text-white";
        case "done":
          return "bg-green-500 text-white";
        default:
          return "bg-gray-200 text-gray-700";
      }
    },
  },
};
</script>

<style scoped>
::v-deep .row-scheduled {
  background-color: lightgreen !important;
}

::v-deep .row-warning {
  color: orange !important;
}

::v-deep .row-danger {
  color: red !important;
}
</style>
